import { supabase } from './supabase';
import type { Machine } from '@jigflow/shared';

export async function getMachines(workspaceId: string): Promise<Machine[]> {
  const { data, error } = await supabase
    .from('machines')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    name: row.name,
    width: parseFloat(row.width),
    height: parseFloat(row.height),
    maxSpeed: row.max_speed ? parseFloat(row.max_speed) : 600,
    lasers: row.lasers ? (Array.isArray(row.lasers) ? row.lasers : []) : [],
    active: row.is_active || false,
    type: row.type || undefined,
    ip: row.ip || undefined,
    port: row.port || undefined,
    software: row.software || 'xtool',
    controllerType: row.controller_type || 'grbl',
  }));
}

export async function getMachine(id: string): Promise<Machine | null> {
  const { data, error } = await supabase
    .from('machines')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    name: data.name,
    width: parseFloat(data.width),
    height: parseFloat(data.height),
    maxSpeed: data.max_speed ? parseFloat(data.max_speed) : 600,
    lasers: data.lasers ? (Array.isArray(data.lasers) ? data.lasers : []) : [],
    active: data.is_active || false,
    type: data.type || undefined,
    ip: data.ip || undefined,
    port: data.port || undefined,
    software: data.software || 'xtool',
    controllerType: data.controller_type || 'grbl',
  };
}

export async function createMachine(workspaceId: string, data: Partial<Machine>): Promise<Machine> {
  const { data: created, error } = await supabase
    .from('machines')
    .insert({
      workspace_id: workspaceId,
      name: data.name || '',
      type: data.type || 'diode',
      ip: data.ip,
      port: data.port,
      width: data.width || 0,
      height: data.height || 0,
      max_speed: data.maxSpeed,
      lasers: data.lasers,
      is_active: data.active || false,
      software: data.software || 'xtool',
      controller_type: data.controllerType || 'grbl',
    })
    .select()
    .single();

  if (error) throw error;

  return {
    id: created.id,
    name: created.name,
    width: parseFloat(created.width),
    height: parseFloat(created.height),
    maxSpeed: created.max_speed ? parseFloat(created.max_speed) : 600,
    lasers: created.lasers ? (Array.isArray(created.lasers) ? created.lasers : []) : [],
    active: created.is_active || false,
    type: created.type || undefined,
    ip: created.ip || undefined,
    port: created.port || undefined,
    software: created.software || 'xtool',
    controllerType: created.controller_type || 'grbl',
  };
}

export async function updateMachine(id: string, data: Partial<Machine>): Promise<Machine> {
  const updateData: Record<string, any> = {};
  if (data.name !== undefined) updateData.name = data.name;
  if (data.type !== undefined) updateData.type = data.type;
  if (data.ip !== undefined) updateData.ip = data.ip;
  if (data.port !== undefined) updateData.port = data.port;
  if (data.width !== undefined) updateData.width = data.width;
  if (data.height !== undefined) updateData.height = data.height;
  if (data.maxSpeed !== undefined) updateData.max_speed = data.maxSpeed;
  if (data.lasers !== undefined) updateData.lasers = data.lasers;
  if (data.active !== undefined) updateData.is_active = data.active;
  if (data.software !== undefined) updateData.software = data.software;
  if (data.controllerType !== undefined) updateData.controller_type = data.controllerType;

  const { data: updated, error } = await supabase
    .from('machines')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  return {
    id: updated.id,
    name: updated.name,
    width: parseFloat(updated.width),
    height: parseFloat(updated.height),
    maxSpeed: updated.max_speed ? parseFloat(updated.max_speed) : 600,
    lasers: updated.lasers ? (Array.isArray(updated.lasers) ? updated.lasers : []) : [],
    active: updated.is_active || false,
    type: updated.type || undefined,
    ip: updated.ip || undefined,
    port: updated.port || undefined,
    software: updated.software || 'xtool',
    controllerType: updated.controller_type || 'grbl',
  };
}

export async function deleteMachine(id: string): Promise<void> {
  const { error } = await supabase
    .from('machines')
    .delete()
    .eq('id', id);

  if (error) throw error;
}
